import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, ElementRef, ViewChild, AfterViewInit, ChangeDetectorRef, NgZone, OnChanges, SimpleChanges, ViewEncapsulation } from '@angular/core';

export interface CoachMarkConfig {
    id: string;
    title?: string;
    body: string;
    ctaText: string;
    targetSelector?: string;
    targets?: {
        selector: string;
        arrowPosition?: 'top' | 'bottom' | 'left' | 'right' | 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left';
        highlightShape?: 'circle' | 'rectangle';
        highlightPadding?: number;
    }[];
    arrowPosition?: 'top' | 'bottom' | 'left' | 'right' | 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left';
    highlightShape?: 'circle' | 'rectangle';
    highlightPadding?: number;
    hideArrow?: boolean;
    tooltipPosition?: 'top' | 'bottom' | 'center' | 'dynamic';
}

@Component({
    selector: 'app-coach-mark',
    templateUrl: './coach-mark.component.html',
    styleUrls: ['./coach-mark.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class CoachMarkComponent implements OnInit, AfterViewInit, OnDestroy, OnChanges {
    @Input() config: CoachMarkConfig;
    @Output() dismissed = new EventEmitter<string>();

    @ViewChild('tooltipContainer') tooltipContainer: ElementRef;

    targetRects: (DOMRect | null)[] = [];
    tooltipPosition: { top: string; left: string; right: string; bottom: string } = {
        top: 'auto',
        left: '50%',
        right: 'auto',
        bottom: '35%'
    };
    arrowPaths: string[] = [];
    isVisible: boolean = false;
    overlayRect: { top: number; left: number } = { top: 0, left: 0 };
    overlayPath: string = '';

    private resizeObserver: ResizeObserver | null = null;
    private animationFrameId: number | null = null;
    private isDestroyed = false;
    private lastTooltipRect: DOMRect | null = null;

    constructor(
        private cdr: ChangeDetectorRef,
        private ngZone: NgZone,
        private el: ElementRef
    ) { }

    ngOnInit() {
        // Move to body to bypass stacking context issues (e.g. Ionic Action Sheets)
        document.body.appendChild(this.el.nativeElement);

        // Add class to body to disable tabs interaction
        document.body.classList.add('coach-mark-active');
        this.isVisible = true;
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.config && !changes.config.firstChange) {
            // Recalculate if config changes (e.g. target selector)
            this.calculatePositions();
        }
    }

    ngAfterViewInit() {
        // Start the position update loop
        this.ngZone.runOutsideAngular(() => {
            this.startPositionLoop();
        });
    }

    ngOnDestroy() {
        this.isDestroyed = true;

        // Remove from body
        if (this.el.nativeElement.parentNode === document.body) {
            document.body.removeChild(this.el.nativeElement);
        }

        // Remove class from body to re-enable tabs interaction
        document.body.classList.remove('coach-mark-active');

        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }

        this.stopPositionLoop();
    }

    startPositionLoop() {
        const update = () => {
            if (this.isDestroyed) return;

            this.calculatePositions();
            this.animationFrameId = requestAnimationFrame(update);
        };

        this.animationFrameId = requestAnimationFrame(update);
    }

    stopPositionLoop() {
        if (this.animationFrameId !== null) {
            cancelAnimationFrame(this.animationFrameId);
            this.animationFrameId = null;
        }
    }

    calculatePositions() {
        const targets = this.config?.targets || (this.config?.targetSelector ? [{
            selector: this.config.targetSelector,
            arrowPosition: this.config.arrowPosition,
            highlightShape: this.config.highlightShape,
            highlightPadding: this.config.highlightPadding
        }] : []);

        if (targets.length === 0) {
            // If no target, center the tooltip
            this.updateTooltipPosition({
                top: 'auto',
                left: '50%',
                right: 'auto',
                bottom: '35%'
            });
            this.targetRects = [];
            this.arrowPaths = [];
            return;
        }

        const newTargetRects: (DOMRect | null)[] = [];
        let hasChanges = false;

        targets.forEach((target, index) => {
            const targetElement = document.querySelector(target.selector);
            if (!targetElement) {
                newTargetRects.push(null);
                return;
            }

            const newRect = targetElement.getBoundingClientRect();
            newTargetRects.push(newRect);

            const oldRect = this.targetRects[index];
            if (!oldRect ||
                Math.abs(oldRect.top - newRect.top) > 1 ||
                Math.abs(oldRect.left - newRect.left) > 1 ||
                Math.abs(oldRect.width - newRect.width) > 1 ||
                Math.abs(oldRect.height - newRect.height) > 1) {
                hasChanges = true;
            }
        });

        // Also check if tooltip itself moved (e.g. after a position change)
        const tooltipEl = this.tooltipContainer?.nativeElement;
        let tooltipRect: DOMRect | null = null;
        if (tooltipEl) {
            tooltipRect = tooltipEl.getBoundingClientRect();
            if (!this.lastTooltipRect ||
                Math.abs(this.lastTooltipRect.top - tooltipRect.top) > 1 ||
                Math.abs(this.lastTooltipRect.left - tooltipRect.left) > 1) {
                hasChanges = true;
            }
        }

        if (!hasChanges && newTargetRects.length === this.targetRects.length) {
            return;
        }

        this.targetRects = newTargetRects;
        this.lastTooltipRect = tooltipRect;

        const overlayEl = document.querySelector('.coach-mark-overlay');
        this.overlayRect = overlayEl ? overlayEl.getBoundingClientRect() : { top: 0, left: 0 };

        // Run update in Angular zone only when values change
        this.ngZone.run(() => {
            this.calculateTooltipPosition();
            this.calculateArrowPaths();
            this.calculateOverlayPath();
            this.cdr.detectChanges();
        });
    }

    calculateOverlayPath() {
        const w = window.innerWidth;
        const h = window.innerHeight;
        // The outer screen rectangle
        let path = `M 0 0 h ${w} v ${h} h -${w} Z `;

        const overlayEl = document.querySelector('.coach-mark-overlay');
        const overlayRect = overlayEl ? overlayEl.getBoundingClientRect() : { top: 0, left: 0 };

        const targets = this.config?.targets || (this.config?.targetSelector ? [{
            selector: this.config.targetSelector,
            highlightShape: this.config.highlightShape,
            highlightPadding: this.config.highlightPadding
        }] : []);

        this.targetRects.forEach((rect, index) => {
            if (!rect) return;
            const target = targets[index];
            const padding = target?.highlightPadding ?? this.config?.highlightPadding ?? 8;
            const shape = target?.highlightShape ?? this.config?.highlightShape ?? 'rectangle';

            const relTop = rect.top - overlayRect.top;
            const relLeft = rect.left - overlayRect.left;

            if (shape === 'circle') {
                const size = Math.max(rect.width, rect.height) + padding * 2;
                const r = size / 2;
                const cx = relLeft + rect.width / 2;
                const cy = relTop + rect.height / 2;

                // Draw circle (counter-clockwise)
                path += `M ${cx} ${cy - r} A ${r} ${r} 0 0 0 ${cx} ${cy + r} A ${r} ${r} 0 0 0 ${cx} ${cy - r} Z `;
            } else {
                const rx = 8;
                const ry = 8;
                const x = relLeft - padding;
                const y = relTop - padding;
                const width = rect.width + padding * 2;
                const height = rect.height + padding * 2;

                path += `M ${x + rx} ${y} h ${width - rx * 2} a ${rx} ${ry} 0 0 1 ${rx} ${ry} v ${height - ry * 2} a ${rx} ${ry} 0 0 1 -${rx} ${ry} h -${width - rx * 2} a ${rx} ${ry} 0 0 1 -${rx} -${ry} v -${height - ry * 2} a ${rx} ${ry} 0 0 1 ${rx} -${ry} Z `;
            }
        });

        this.overlayPath = path;
    }

    updateTooltipPosition(newPos: { top: string; left: string; right: string; bottom: string }) {
        if (JSON.stringify(this.tooltipPosition) !== JSON.stringify(newPos)) {
            this.ngZone.run(() => {
                this.tooltipPosition = newPos;
                this.cdr.detectChanges();
            });
        }
    }

    calculateTooltipPosition() {
        const isMobile = window.innerWidth < 768;
        const pos = this.config?.tooltipPosition || 'bottom';

        if (pos === 'dynamic' && this.targetRects.length > 0) {
            const target = this.targetRects[0];
            const overlayTop = this.overlayRect?.top || 0;
            
            // Constrain left position to keep tooltip on screen
            let tWidth = 340; // Default mobile
            if (window.innerWidth >= 1024) tWidth = 400; // Desktop
            else if (window.innerWidth >= 768) tWidth = 380; // Tablet
            
            const minLeft = (tWidth / 2) + 16;
            const maxLeft = window.innerWidth - (tWidth / 2) - 16;
            
            let targetCenterX = target.left + (target.width / 2);
            let boundedLeft = Math.max(minLeft, Math.min(targetCenterX, maxLeft));

            // Vertical awareness: check if it fits below
            const estimatedTHeight = 120; // Avg height with body text
            const spaceBelow = window.innerHeight - target.bottom;
            
            if (spaceBelow < (estimatedTHeight + 40)) {
                // Not enough space below - show ABOVE
                this.tooltipPosition = {
                    top: 'auto',
                    left: boundedLeft + 'px',
                    right: 'auto',
                    bottom: (window.innerHeight - target.top + 30) + 'px'
                };
                // Also update config dynamically for arrow rendering if needed, 
                // but calculateArrowPaths already uses centers.
            } else {
                // Default BELOW
                this.tooltipPosition = {
                    top: (target.bottom - overlayTop + 30) + 'px',
                    left: boundedLeft + 'px',
                    right: 'auto',
                    bottom: 'auto'
                };
            }
        } else if (pos === 'top') {
            this.tooltipPosition = {
                top: isMobile ? '80px' : '100px',
                left: '50%',
                right: 'auto',
                bottom: 'auto'
            };
        } else if (pos === 'center') {
            this.tooltipPosition = {
                top: '50%',
                left: '50%',
                right: 'auto',
                bottom: 'auto'
            };
        } else {
            // Default bottom
            this.tooltipPosition = {
                top: 'auto',
                left: '50%',
                right: 'auto',
                bottom: isMobile ? '20%' : '25%'
            };
        }
    }

    calculateArrowPaths() {
        if (this.targetRects.length === 0 || this.config?.hideArrow) {
            this.arrowPaths = [];
            return;
        }

        const overlayEl = document.querySelector('.coach-mark-overlay');
        const overlayRect = overlayEl ? overlayEl.getBoundingClientRect() : { top: 0, left: 0 };

        const tooltipEl = this.tooltipContainer?.nativeElement;
        if (!tooltipEl) return;

        const tooltipRect = tooltipEl.getBoundingClientRect();
        const tWidth = tooltipRect.width;
        const tHeight = tooltipRect.height;
        const tLeft = tooltipRect.left - overlayRect.left;
        const tTop = tooltipRect.top - overlayRect.top;
        const tCenterX = tLeft + tWidth / 2;
        const tCenterY = tTop + tHeight / 2;

        const targets = this.config?.targets || (this.config?.targetSelector ? [{
            selector: this.config.targetSelector,
            arrowPosition: this.config.arrowPosition
        }] : []);

        this.arrowPaths = this.targetRects.map((rect, index) => {
            if (!rect) return '';

            const target = targets[index];
            const isMobileTarget = target.selector && target.selector.includes('mobile');

            // Relative target center
            const targetCenterX = (rect.left - overlayRect.left) + rect.width / 2;
            const targetCenterY = (rect.top - overlayRect.top) + rect.height / 2;
            const targetWidth = rect.width;
            const targetHeight = rect.height;

            // Vector from tooltip center to target center
            const dx = targetCenterX - tCenterX;
            const dy = targetCenterY - tCenterY;
            const dist = Math.sqrt(dx * dx + dy * dy);

            if (dist < 10) return ''; // Too close

            // 1. Calculate START point (outside tooltip)
            let tScale;
            if (Math.abs(dx) * (tHeight / 2) > Math.abs(dy) * (tWidth / 2)) {
                tScale = (tWidth / 2) / Math.abs(dx);
            } else {
                tScale = (tHeight / 2) / Math.abs(dy);
            }

            // 2. Calculate END point (outside target rect)
            let targetScale;
            if (Math.abs(dx) * (targetHeight / 2) > Math.abs(dy) * (targetWidth / 2)) {
                targetScale = (targetWidth / 2) / Math.abs(dx);
            } else {
                targetScale = (targetHeight / 2) / Math.abs(dy);
            }

            // Calculate actual gap distance between edges
            const distFromCenterToTooltipEdge = dist * tScale;
            const distFromCenterToTargetEdge = dist * targetScale;
            const gap = dist - distFromCenterToTooltipEdge - distFromCenterToTargetEdge;

            // If gap is too small, don't show arrow to avoid artifacts
            if (gap < 18) return '';

            // Dynamic padding for small gaps
            let tPadding = 12;
            const highlightPadding = target.highlightPadding || 8;
            let arrowTargetMargin = highlightPadding + 6;

            if (gap < 40) {
                // Reduce paddings for tighter spaces
                tPadding = Math.max(2, gap * 0.2);
                // Reduce the extra margin outside the highlight, but keep at least 2px
                arrowTargetMargin = highlightPadding + Math.max(2, (gap - 18));
            }

            const startX = tCenterX + dx * tScale + (dx / dist) * tPadding;
            const startY = tCenterY + dy * tScale + (dy / dist) * tPadding;

            const endX = targetCenterX - (dx / dist) * (targetScale * dist + arrowTargetMargin);
            const endY = targetCenterY - (dy / dist) * (targetScale * dist + arrowTargetMargin);

            // Control points for the curve
            // We want the curve to be slightly dynamic based on the relative positions
            const midX = (startX + endX) / 2;
            const midY = (startY + endY) / 2;

            // Check for vertical alignment (straight line)
            if (Math.abs(dx) < 20) {
                return `M ${startX} ${startY} L ${endX} ${endY}`;
            }

            // Add a slight bulge to the curve
            const controlOffset = Math.min(dist * 0.2, 50);
            const controlX = midX + (dx / dist) * 0.1; // Slighting shifting toward target direction
            const controlY = midY - controlOffset; // Standard upward curve

            return `M ${startX} ${startY} Q ${controlX} ${controlY} ${endX} ${endY}`;
        });
    }

    getHighlightStyle(index: number): { [key: string]: string } {
        const rect = this.targetRects[index];
        if (!rect) {
            return { display: 'none' };
        }

        const overlayEl = document.querySelector('.coach-mark-overlay');
        const overlayRect = overlayEl ? overlayEl.getBoundingClientRect() : { top: 0, left: 0 };

        const targets = this.config?.targets || (this.config?.targetSelector ? [{
            selector: this.config.targetSelector,
            highlightShape: this.config.highlightShape,
            highlightPadding: this.config.highlightPadding
        }] : []);

        const target = targets[index];
        const padding = target.highlightPadding || 8;
        const shape = target.highlightShape || 'rectangle';

        // Relative coordinates
        const relTop = rect.top - overlayRect.top;
        const relLeft = rect.left - overlayRect.left;

        if (shape === 'circle') {
            const size = Math.max(rect.width, rect.height) + padding * 2;
            return {
                top: `${relTop + rect.height / 2 - size / 2}px`,
                left: `${relLeft + rect.width / 2 - size / 2}px`,
                width: `${size}px`,
                height: `${size}px`,
                'border-radius': '50%'
            };
        } else {
            return {
                top: `${relTop - padding}px`,
                left: `${relLeft - padding}px`,
                width: `${rect.width + padding * 2}px`,
                height: `${rect.height + padding * 2}px`,
                'border-radius': '8px'
            };
        }
    }

    onCtaClick() {
        this.dismissed.emit(this.config.id);
    }

    onOverlayClick(event: Event) {
        // Tap out: dismiss when clicking outside the tooltip
        this.dismissed.emit(this.config.id);
    }
}
